import React from 'react';
import { ArrowLeft, Building2, Sparkles, MessageCircle, CalendarCheck, ArrowUpRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';

export const RealEstateDemo = () => {
  const navigate = useNavigate();

  const steps = [
    {
      title: 'Smart Lead Capture',
      description: 'AI chatbot qualifies buyers and sellers 24/7 across WhatsApp, Instagram, and web.'
    },
    {
      title: 'Auto Follow-Ups',
      description: 'Instant replies, reminders, and property details sent automatically to keep interest hot.'
    },
    {
      title: 'Booking Automation',
      description: 'Property visits and calls scheduled with automated confirmations and reminders.'
    },
    {
      title: 'CRM Sync',
      description: 'Every lead lands inside Google Sheets or HubSpot with tags, budgets, and timelines.'
    }
  ];

  const automationTimeline = [
    'Lead comes in from ad or website.',
    'AI chatbot qualifies budget, location, and timeline.',
    'Auto follow-up with curated listings + brochure.',
    'Agent receives hot lead alert + meeting booked.'
  ];

  const results = [
    { label: 'Lead response time', value: '< 1 min' },
    { label: 'Weekly hours saved', value: '10-15 hrs' },
    { label: 'Qualified lead rate', value: '+40%' }
  ];

  const listings = [
    {
      title: 'Skyline Residences',
      meta: '3 BHK · 1680 sq ft · Gurgaon',
      price: 'INR 1.45 Cr'
    },
    {
      title: 'Harbor Heights',
      meta: '2 BHK · 1240 sq ft · Mumbai',
      price: 'INR 98 L'
    },
    {
      title: 'Palm Crest Villas',
      meta: '4 BHK · 2450 sq ft · Pune',
      price: 'INR 2.1 Cr'
    }
  ];

  return (
    <div className="relative min-h-screen bg-neutral-950 text-white overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute top-24 right-10 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-white/5 blur-3xl" />
      </div>

      <header className="relative z-10 border-b border-white/10 bg-neutral-950/80 backdrop-blur-xl">
        <div className="max-w-6xl lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                <Building2 size={20} className="text-white/80" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.3em] text-white/50">Demo</p>
                <p className="text-lg font-semibold">Real Estate AI Automation</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl lg:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <section className="py-20 text-center space-y-6">
          <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full">
            AI Automation Demo for Realtors
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight">
            Convert property inquiries into booked site visits in minutes.
          </h1>
          <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto">
            This demo shows how an AI-powered system captures leads, qualifies buyers, and schedules visits automatically —
            all while your team stays focused on closing deals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-white text-neutral-900 hover:bg-white/90 px-7 py-6"
              onClick={() => window.open('https://calendly.com/pranshik/build-your-mvp-discussion', '_blank', 'noopener,noreferrer')}
            >
              Book Free Automation Audit
            </Button>
            <button
              className="text-sm font-medium text-white/80 hover:text-white underline decoration-white/40 underline-offset-4"
              onClick={() => window.open('https://wa.me/919805117900', '_blank', 'noopener,noreferrer')}
            >
              WhatsApp Chat
            </button>
          </div>
        </section>

        <section className="py-16 border-t border-white/10">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {steps.map((step) => (
              <Card key={step.title} className="bg-white/5 border border-white/10 rounded-2xl">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center gap-2 text-white/70">
                    <Sparkles size={16} />
                    <p className="text-sm uppercase tracking-[0.2em]">Automation</p>
                  </div>
                  <h3 className="text-lg font-semibold">{step.title}</h3>
                  <p className="text-sm text-white/70">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="py-16 border-t border-white/10">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] items-start">
            <div className="space-y-4">
              <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">
                Live Automation Flow
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-semibold">How the system works</h2>
              <p className="text-white/60 max-w-2xl">
                Every inquiry is handled automatically, keeping your sales team focused on high-intent prospects.
              </p>
              <div className="space-y-3">
                {automationTimeline.map((item, index) => (
                  <div key={item} className="flex items-start gap-3 text-sm text-white/70">
                    <span className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-white/80 text-xs">
                      {index + 1}
                    </span>
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <Card className="bg-white/5 border border-white/10 rounded-2xl">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                    <MessageCircle size={18} className="text-white/80" />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-[0.2em] text-white/60">AI Chat Preview</p>
                    <p className="text-lg font-semibold">24/7 Lead Qualifier</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm text-white/70">
                  <div className="rounded-2xl bg-white/5 p-4 border border-white/10">
                    Hi! Are you looking to buy or sell a property?
                  </div>
                  <div className="rounded-2xl bg-white/10 p-4 border border-white/10 text-white">
                    Buying. My budget is around INR 1.5 Cr.
                  </div>
                  <div className="rounded-2xl bg-white/5 p-4 border border-white/10">
                    Perfect. Which location do you prefer and when can we schedule a site visit?
                  </div>
                </div>
                <Button className="w-full bg-white text-neutral-900 hover:bg-white/90">
                  View Full Chatbot Demo
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 border-t border-white/10">
          <div className="grid gap-6 md:grid-cols-3">
            {results.map((result) => (
              <Card key={result.label} className="bg-white/5 border border-white/10 rounded-2xl text-center">
                <CardContent className="p-6 space-y-2">
                  <p className="text-2xl font-semibold">{result.value}</p>
                  <p className="text-sm text-white/60">{result.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="py-16 border-t border-white/10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="space-y-3">
              <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">
                Listings Showcase
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-semibold">Your leads see premium listings instantly</h2>
              <p className="text-white/60 max-w-2xl">
                The AI sends curated listings automatically based on the buyer&apos;s budget, location, and preferences.
              </p>
            </div>
            <button className="text-sm font-medium text-white/80 hover:text-white underline decoration-white/40 underline-offset-4">
              Browse all listings
            </button>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {listings.map((listing) => (
              <Card key={listing.title} className="bg-white/5 border border-white/10 rounded-2xl hover-card">
                <CardContent className="p-6 space-y-4">
                  <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/10 h-36 flex items-center justify-center">
                    <Building2 size={36} className="text-white/60" />
                    <div className="absolute top-3 right-3">
                      <ArrowUpRight size={16} className="text-white/60" />
                    </div>
                  </div>
                  <div>
                    <p className="text-lg font-semibold">{listing.title}</p>
                    <p className="text-sm text-white/60">{listing.meta}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-white/70">{listing.price}</p>
                    <Button size="sm" className="bg-white text-neutral-900 hover:bg-white/90">
                      Enquire
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="py-16 border-t border-white/10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="space-y-3 max-w-2xl">
              <Badge className="bg-white/10 text-white border-white/10 px-4 py-2 rounded-full w-fit">
                Ready to automate?
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-semibold">Let&apos;s launch your real estate automation in 7 days.</h2>
              <p className="text-white/60">
                Get the chatbot, CRM setup, and follow-up sequences configured for your team with full support.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                className="bg-white text-neutral-900 hover:bg-white/90"
                onClick={() => window.open('https://calendly.com/pranshik/build-your-mvp-discussion', '_blank', 'noopener,noreferrer')}
              >
                Book Free Consultation
              </Button>
              <button
                className="text-sm font-medium text-white/80 hover:text-white underline decoration-white/40 underline-offset-4"
                onClick={() => window.open('https://wa.me/919805117900', '_blank', 'noopener,noreferrer')}
              >
                WhatsApp Chat
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};
